import { Injectable } from '@angular/core';
import { forkJoin, map, tap, Observable, of, shareReplay, catchError, switchMap } from 'rxjs';
import {
  StrategyItem,
  RawStrategyItem,
  RecommendedStrategies,
  RecommendedStrategiesResponse,
  parseStrategyItem,
  CustomStrategyResponse,
  CreateCustomStrategyPayload,
  STRATEGY_CATALOG_BASE_PATH,
  customStrategyToStrategyItem,
  isCustomStrategy,
} from '../strategy-catalog.models';
import { ApiService } from '@app/core/services/api/api.service';
import { StrategyColumns } from '@pdqdev/pdq-shared/dist/entities/StrategyCatalog';

@Injectable({
  providedIn: 'root',
})
export class StrategyCatalogService {
  private cache$: Observable<StrategyItem[]> | null = null;
  private maxxCache$: Observable<RecommendedStrategies> | null = null;
  private customCache$: Observable<CustomStrategyResponse[]> | null = null;
  // Resolved out-of-catalog (deprecated) strategies, keyed by id. Their data is historical and stable within a
  // session, so once resolved we never re-request them — and a later flaky /resolve falls back to these.
  private resolvedCache = new Map<string, StrategyItem>();

  constructor(private apiService: ApiService) {}

  getStrategies(): Observable<StrategyItem[]> {
    if (!this.cache$) {
      this.cache$ = this.apiService.get('strategy-catalog/data').pipe(
        map((items: RawStrategyItem[]) =>
          items.map(parseStrategyItem).filter((item): item is StrategyItem => item !== null),
        ),
        shareReplay(1),
      );
    }
    return this.cache$;
  }

  getStrategy(id: string): Observable<StrategyItem | undefined> {
    return this.getStrategies().pipe(
      switchMap((items) => {
        const found = items.find((item) => item[StrategyColumns.ID] === id);
        if (found) return of(found);
        return isCustomStrategy(id) ? of(undefined) : this.resolveStrategy(id);
      }),
    );
  }

  private resolveStrategy(id: string): Observable<StrategyItem | undefined> {
    const cached = this.resolvedCache.get(id);
    if (cached) {
      return of(cached);
    }
    return this.apiService.get(`${STRATEGY_CATALOG_BASE_PATH}/resolve/${id}`).pipe(
      map((res: { strategy: RawStrategyItem; deprecated: boolean }) => {
        const parsed = parseStrategyItem(res.strategy);
        if (parsed) {
          parsed.deprecated = res.deprecated;
          this.resolvedCache.set(parsed[StrategyColumns.ID], parsed);
        }
        return parsed ?? undefined;
      }),
      catchError(() => of(undefined)),
    );
  }

  resolveStrategies(ids: string[]): Observable<StrategyItem[]> {
    if (ids.length === 0) {
      return of([]);
    }
    const cached = ids.map((id) => this.resolvedCache.get(id)).filter((item): item is StrategyItem => !!item);
    const missing = ids.filter((id) => !this.resolvedCache.has(id));
    if (missing.length === 0) {
      return of(cached);
    }
    return this.apiService.post(`${STRATEGY_CATALOG_BASE_PATH}/resolve`, { ids: missing }).pipe(
      map((entries: { strategyId: string; strategy: RawStrategyItem; deprecated: boolean }[]) =>
        (entries ?? [])
          .map((entry) => {
            const parsed = parseStrategyItem(entry.strategy);
            if (parsed) {
              parsed.deprecated = entry.deprecated;
              this.resolvedCache.set(parsed[StrategyColumns.ID], parsed);
            }
            return parsed;
          })
          .filter((item): item is StrategyItem => !!item),
      ),
      map((fetched) => [...cached, ...fetched]),
      catchError(() => of(cached)),
    );
  }

  getRecommendedStrategies(isEssentials = false): Observable<RecommendedStrategies> {
    if (!this.maxxCache$) {
      this.maxxCache$ = this.apiService
        .get('strategy-catalog/recommended-strategies', {
          params: { shopId: this.apiService.shopId, ...(isEssentials && { isEssentials: true }) },
        })
        .pipe(
          map((response: RecommendedStrategiesResponse) => {
            if (response && response.success && response?.data) {
              const ids = response.data.ids;
              return { ids: typeof ids === 'number' ? String(ids) : (ids ?? '') };
            }
            throw new Error(response?.message || 'Failed to load recommended strategies');
          }),
          catchError(() => {
            this.maxxCache$ = null;
            return of({ ids: '' } as RecommendedStrategies);
          }),
          shareReplay(1),
        );
    }
    return this.maxxCache$;
  }

  getAllStrategies(): Observable<StrategyItem[]> {
    return forkJoin([
      this.getStrategies(),
      this.getCustomStrategies().pipe(catchError(() => of([] as CustomStrategyResponse[]))),
    ]).pipe(
      map(([strategies, customStrategies]) => [...strategies, ...customStrategies.map(customStrategyToStrategyItem)]),
    );
  }

  getCustomStrategies(): Observable<CustomStrategyResponse[]> {
    if (!this.customCache$) {
      this.customCache$ = this.apiService.get(`${STRATEGY_CATALOG_BASE_PATH}/custom-strategies`).pipe(shareReplay(1));
    }
    return this.customCache$;
  }

  createCustomStrategy(payload: CreateCustomStrategyPayload): Observable<CustomStrategyResponse> {
    return this.apiService
      .post(`${STRATEGY_CATALOG_BASE_PATH}/custom-strategies`, payload)
      .pipe(tap(() => (this.customCache$ = null)));
  }

  deleteCustomStrategy(id: string): Observable<void> {
    return this.apiService.delete(`${STRATEGY_CATALOG_BASE_PATH}/custom-strategies/${id}`).pipe(
      tap(() => (this.customCache$ = null)),
      map(() => undefined),
    );
  }

  uploadPreviewImage(file: File): Observable<{ key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.apiService.postFormData(`${STRATEGY_CATALOG_BASE_PATH}/custom-strategies/upload-preview`, formData);
  }
}
